// src/pages/personal/analytics/MarketingExpensesSection/components/IntegrationSettings.tsx
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { Card, Flex, Grid } from '@/components/ui/Layout';
import { api } from '@/services/api';
import { useBusinessId } from '@/hooks/useBusinessId';

interface IntegrationSettingsProps {
  onSuccess?: () => void;
}

// Типы интеграций
const INTEGRATION_TYPES = [
  {
    id: 'instagram',
    name: 'Instagram',
    icon: '📸',
    description: 'Подключите рекламный кабинет Instagram для автоматического импорта расходов',
    isAvailable: true
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: '👤',
    description: 'Подключите рекламный кабинет Facebook для автоматического импорта расходов',
    isAvailable: true
  },
  {
    id: 'google',
    name: 'Google Ads',
    icon: '🔍',
    description: 'Подключите рекламный кабинет Google Ads для автоматического импорта расходов',
    isAvailable: false
  },
  {
    id: '2gis',
    name: '2ГИС',
    icon: '🗺️',
    description: 'Подключите рекламный кабинет 2ГИС для автоматического импорта расходов',
    isAvailable: false
  }
];

const IntegrationSettings: React.FC<IntegrationSettingsProps> = ({
  onSuccess
}) => {
  const { t } = useTranslation(['expenses', 'common']);
  const { businessId } = useBusinessId();
  
  // Состояния
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Обработчик открытия модального окна
  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
    setError(null);
  }, []);
  
  // Обработчик закрытия модального окна
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedIntegration(null);
    setError(null);
  }, []);
  
  // Обработчик выбора интеграции
  const handleSelectIntegration = useCallback((integrationId: string) => {
    setSelectedIntegration(integrationId);
  }, []);
  
  // Обработчик подключения интеграции
  const handleConnectIntegration = useCallback(async () => {
    if (!selectedIntegration || !businessId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // В реальном приложении здесь будет переход на страницу авторизации OAuth
      // Для демонстрации просто имитируем запрос к API
      const response = await api.post('/api/analytics/marketing/integrations/connect/', {
        business: businessId.toString(),
        integration_type: selectedIntegration
      });
      
      // Если успешно, обновляем страницу или показываем сообщение
      if (response && response.success) {
        if (onSuccess) {
          onSuccess();
        }
        handleCloseModal();
      } else {
        setError(t('marketing.integrations.connection_error'));
      }
    } catch (error: any) {
      console.error('Ошибка подключения интеграции:', error);
      setError(error.message || t('marketing.integrations.unknown_error'));
    } finally {
      setIsLoading(false);
    }
  }, [selectedIntegration, businessId, t, onSuccess, handleCloseModal]);
  
  return (
    <>
      <button
        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 w-full md:w-auto"
        onClick={handleOpenModal}
      >
        {t('marketing.integrations.connect')}
      </button>
      
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        size="4xl"
        mobileMode="fullscreen"
        allowBackdropClose={true}
      >
        <ModalHeader onClose={handleCloseModal}>
          {t('marketing.integrations.title')}
        </ModalHeader>
        
        <ModalBody scrollable={true}>
          {error && (
            <div className="bg-red-100 text-red-800 p-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}
          
          <p className="text-gray-600 mb-6">
            {t('marketing.integrations.description')}
          </p>
          
          <Grid cols={2} mobileCols={1} gap="lg">
            {INTEGRATION_TYPES.map(integration => (
              <Card
                key={integration.id}
                padding="md"
                shadow="sm"
                className={`cursor-pointer transition-all ${
                  selectedIntegration === integration.id 
                    ? 'border-2 border-indigo-500' 
                    : 'border border-gray-200 hover:border-indigo-300'
                } ${!integration.isAvailable ? 'opacity-50' : ''}`}
                onClick={() => integration.isAvailable && handleSelectIntegration(integration.id)}
              >
                <Flex gap="md" align="start">
                  <div className="text-2xl">{integration.icon}</div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {integration.name}
                      {!integration.isAvailable && (
                        <span className="ml-2 text-xs font-medium py-1 px-2 bg-gray-200 text-gray-700 rounded-full">
                          {t('marketing.integrations.coming_soon')}
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {integration.description}
                    </p>
                  </div>
                </Flex>
              </Card>
            ))}
          </Grid>
          
          {selectedIntegration && (
            <div className="mt-6 border-t pt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('marketing.integrations.connection_instructions')}
              </h3>
              
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-sm text-gray-600 mb-2">
                  {t('marketing.integrations.instructions_step_1')}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  {t('marketing.integrations.instructions_step_2')}
                </p>
                <p className="text-sm text-gray-600">
                  {t('marketing.integrations.instructions_step_3')}
                </p>
              </div>
            </div>
          )}
        </ModalBody>
        
        <ModalFooter>
          <Flex justify="end" gap="sm">
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              onClick={handleCloseModal}
            >
              {t('common:cancel')}
            </button>
            <button
              type="button"
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              onClick={handleConnectIntegration}
              disabled={isLoading || !selectedIntegration}
            >
              {isLoading ? t('common:processing') : t('marketing.integrations.connect_button')}
            </button>
          </Flex>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default IntegrationSettings;